import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageProvider';
import '@/styles/vani-splash.css';

const KEY = 'vani:splash:v1';
const GATES = new Set(['/', '/talk']);

const HOLD_OPEN = 200;
const GLYPH_MS = 38;
const COMMA_MS = 240;
const STOP_MS = 380;
const LINE_BREAK_MS = 340;
const HOLD_IDENTITY = 1800;
const CROSSFADE_MS = 460;
const HOLD_CLOSE = 1400;
const EXIT_MS = 720;
const FONT_WAIT_MS = 800;

const INDIC = /[\u0900-\u097F\u0B80-\u0BFF]/;

export function shouldPlaySplash(pathname, {
  env = process.env.NODE_ENV,
  search = '',
  storage,
} = {}) {
  if (env === 'test') return false;
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  if (!GATES.has(path)) return false;
  if (/(?:^|[?&])splash=0(?:&|$)/.test(search || '')) return false;
  try {
    if ((storage || (typeof sessionStorage !== 'undefined' ? sessionStorage : null))?.getItem(KEY)) {
      return false;
    }
  } catch (_) { /* private mode */ }
  return true;
}

function glyphs(text) {
  const s = String(text || '');
  if (typeof Intl !== 'undefined' && Intl.Segmenter) {
    return [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(s)].map((p) => p.segment);
  }
  return Array.from(s);
}

function msFor(g) {
  if (g === '\n') return LINE_BREAK_MS;
  if (/[?!:]/.test(g)) return STOP_MS;
  if (/[.,،।]/.test(g)) return COMMA_MS;
  if (g === ' ') return 52;
  return GLYPH_MS;
}

function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(Object.assign(new Error('aborted'), { name: 'AbortError' }));
      return;
    }
    const t = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(t);
      reject(Object.assign(new Error('aborted'), { name: 'AbortError' }));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

async function typeInto(setter, text, signal) {
  const parts = glyphs(text);
  let out = '';
  setter('');
  for (let i = 0; i < parts.length; i += 1) {
    out += parts[i];
    setter(out);
    await delay(msFor(parts[i]), signal);
  }
}

async function waitForFonts(signal) {
  if (!document.fonts?.ready) return;
  try {
    await Promise.race([document.fonts.ready, delay(FONT_WAIT_MS, signal)]);
  } catch (e) {
    if (e?.name === 'AbortError') throw e;
  }
}

function reducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function Caret() {
  return <span className="vani-splash-caret" aria-hidden="true" />;
}

function clearBoot() {
  document.documentElement.classList.remove('vani-splash-pending');
  document.getElementById('vani-boot')?.remove();
}

function markSeen() {
  try { sessionStorage.setItem(KEY, '1'); } catch (_) { /* private mode */ }
}

export default function VaniSplash() {
  const { pathname, search } = useLocation();
  const { t } = useLanguage();
  const play = shouldPlaySplash(pathname, { search });
  const [gone, setGone] = useState(!play);
  const [leaving, setLeaving] = useState(false);
  const [typing, setTyping] = useState(false);
  const [act, setAct] = useState('id');
  const [thesisOn, setThesisOn] = useState(false);
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [caretOn, setCaretOn] = useState('line1');
  const leavingRef = useRef(false);
  const packRef = useRef(t);
  const acRef = useRef(null);

  packRef.current = t;

  const dismiss = useCallback((fast) => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    acRef.current?.abort();
    setTyping(false);
    setLeaving(true);
    markSeen();
    window.setTimeout(() => {
      clearBoot();
      setGone(true);
    }, fast ? 280 : EXIT_MS);
  }, []);

  useEffect(() => {
    if (!play) {
      clearBoot();
      return undefined;
    }

    const ac = new AbortController();
    acRef.current = ac;
    const { signal } = ac;
    const pack = packRef.current;
    const skipAnim = reducedMotion();

    const watchdog = window.setTimeout(() => {
      dismiss(true);
    }, 8500);

    (async () => {
      try {
        await waitForFonts(signal);
        if (skipAnim) {
          setThesisOn(true);
          setLine1(pack.splashLine1);
          setLine2(pack.splashLine2);
          setAct('line');
          setCaretOn('line2');
          await delay(900, signal);
          dismiss(true);
          return;
        }

        // Screen 1: Thesis Hook ("Solving Cancer Care in 2026.")
        await delay(HOLD_OPEN, signal);
        setThesisOn(true);
        await delay(HOLD_IDENTITY, signal);

        // Screen 2: Direct Mission Delivery
        setAct('line');
        setCaretOn('line1');
        await delay(CROSSFADE_MS, signal);

        setTyping(true);
        await typeInto(setLine1, pack.splashLine1, signal);
        setCaretOn('line2');
        await delay(LINE_BREAK_MS, signal);
        await typeInto(setLine2, pack.splashLine2, signal);
        setTyping(false);
        await delay(HOLD_CLOSE, signal);
        dismiss(false);
      } catch (e) {
        if (e?.name !== 'AbortError') {
          console.warn('VaniSplash dismissed on animation error:', e);
          dismiss(true);
        }
      }
    })();

    const onKey = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        ac.abort();
        dismiss(true);
      }
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.clearTimeout(watchdog);
      ac.abort();
      acRef.current = null;
      window.removeEventListener('keydown', onKey);
    };
  }, [play, dismiss]);

  if (gone || !play) return null;

  const indic1 = INDIC.test(t.splashLine1);
  const indic2 = INDIC.test(t.splashLine2);

  return (
    <div
      className={`vani-splash${typing ? ' is-typing' : ''}${leaving ? ' is-leaving' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="vani-splash-sr"
      data-testid="vani-splash"
      onClick={() => dismiss(true)}
    >
      <p id="vani-splash-sr" className="vani-splash-sr">{t.splashSr}</p>

      <div className="vani-splash-stack">
        <div className={`vani-splash-act vani-splash-act--thesis${act === 'id' ? '' : ' is-off'}`}>
          <div className={`vani-splash-thesis-wrap${thesisOn ? ' is-visible' : ''}`}>
            <h1 className="vani-splash-thesis">
              {t.splashThesis || 'Solving Cancer Care in 2026.'}
            </h1>
          </div>
        </div>
        <div className={`vani-splash-act${act === 'line' ? '' : ' is-off'}`}>
          <p className={`vani-splash-line${indic1 ? ' is-indic' : ''}`}>
            {line1}
            {caretOn === 'line1' ? <Caret /> : null}
          </p>
          <p className={`vani-splash-line${indic2 ? ' is-indic' : ''}`}>
            {line2}
            {caretOn === 'line2' ? <Caret /> : null}
          </p>
        </div>
      </div>

      <button
        type="button"
        className="vani-splash-skip"
        onClick={(e) => {
          e.stopPropagation();
          dismiss(true);
        }}
      >
        {t.splashSkip}
      </button>
    </div>
  );
}
